import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { AxiosError } from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
} from "@lsf/ui";
import { useAuth } from "@/lib/auth/AuthProvider";

export function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();

  const [identifier, setIdentifier] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const msg = await forgotPassword(identifier);
      setMessage(msg);
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 422) {
        setError("Enter your username or email.");
      } else {
        setError("Something went wrong. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl">
          <CardHeader className="space-y-2">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <KeyRound className="w-6 h-6 text-accent" />
            </div>
            <CardTitle className="text-2xl">Reset your password</CardTitle>
            <CardDescription>
              Enter the username or email you use to sign in, and we'll send reset instructions if we find a match.
              If you hold more than one apartment, use that apartment's username to reset just that login.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {message ? (
              <div className="space-y-4">
                <div
                  role="status"
                  className="rounded-md border border-accent/30 bg-accent/10 px-3 py-2 text-sm text-foreground"
                >
                  {message}
                </div>
                <Link to="/login" className="text-sm text-accent hover:underline">
                  Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                {error && (
                  <div
                    role="alert"
                    className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  >
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="identifier" className="text-sm font-medium">
                    Username or email
                  </Label>
                  <Input
                    id="identifier"
                    type="text"
                    autoComplete="username"
                    required
                    placeholder="e.g. Toja-1T10"
                    className="h-11"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                  />
                </div>

                <Button type="submit" disabled={submitting} className="w-full h-11 mt-2">
                  {submitting ? "Sending…" : "Send reset instructions"}
                </Button>

                <Link to="/login" className="block text-center text-sm text-accent hover:underline">
                  Back to sign in
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
